import React from 'react';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';

import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';

import PieceGestionSearch from 'components/Shared/PieceGestionSearch';
import LigneGestionQualification from 'components/Shared/LigneGestionQualification';
import DepannageDirectClient from 'components/Shared/DepannageDirectClient';
import DropZone from 'components/Shared/DropZone';

import ColisLogistiquePhoto from 'components/Shared/ColisLogistiquePhoto';


import { ReclamationQualificationDetailType } from 'constants.js';
import { ReclamationQualificationDetailCodes } from 'constants.js';
import { AnomalieType } from 'constants.js';
import { TypeCodes } from 'constants.js';

import qualificationHelper from 'utils/qualificationHelper';

const styles = theme => ({
    flexContainer: {
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    row: {
        flexBasis: '100%',
        marginBottom: '10px'
    },
    formControl: {
        margin: 0
    },
    group: {
        margin: theme.spacing(1, 0)
    },
    heightLabel: {
        height: '30px'
    }
});


/* Liste des réponses possibles */
const photoConsulteeLabel = "Avez-vous pris connaissance de la photo ? ";
const photoConsulteeLabels = [
    "Oui, j'ai trouvé les informations souhaitées et tout est conforme (dossier après-vente clôturé automatiquement).",
    "Oui, mais je souhaite créer le dossier après-vente"
];



// Regles de validation à passer en paramètre de qualificationHelper.getErrors
// La fonction d'assertion renvoi true quand la règle est respectée
const _rules = [
    {
        key: ReclamationQualificationDetailCodes.ColisAbimeType,
        errorMsg: 'L\'état du colis n\'a pas été spécifié.',
        fields: 'colisAbimeType',
        assertion: function (a) {
            return a !== undefined && a !== null && a !== ''
        }
    },
    {
        key: ReclamationQualificationDetailCodes.ReserveTransporteurFaite,
        errorMsg: 'Veuillez indiquer si une réserve a été effectuée auprès du transporteur.',
        fields: 'reserveTransporteurFaite',
        assertion: function (a) {
            return a !== undefined && a !== null && a !== ''
        }
    },
    {
        key: ReclamationQualificationDetailCodes.ColisRefuse,
        errorMsg: 'Veuillez indiquer si vous avez refusé un ou plusieurs colis.',
        fields: ['reserveTransporteurFaite', 'colisRefuse'],
        assertion: function (a) {
            if (a[0] === 'false') {
                return a[1] !== undefined && a[1] !== null && a[1] !== ''
            }
            return true; 
        }
    },

    // Règle pour la consultation de la photo
    {
        key: ReclamationQualificationDetailCodes.PhotoConsultee,
        errorMsg: photoConsulteeLabel,
        fields: ['lignes', 'colisLogistiques', 'photoConsultee'],

        assertion: function (a) {
            if (a[0].length > 0 && a[1].length > 0) {

                let reclaCodes = a[0].map(x => x.codeArticle);

                let colisLogistiquesPhoto = a[1].map(x => {

                    let codesArticles = x.lignes.map(x => x.codeArticle);
                    let c = {
                        photoId: x.photoId,
                        numeroSupport: x.numeroSupport,
                        concerneRecla: reclaCodes.some(r => codesArticles.indexOf(r) >= 0)
                    };
                    return c;
                });

                let afficherQuestion = colisLogistiquesPhoto.some(x => x.concerneRecla && x.photoId);


                if (afficherQuestion) {
                    return a[2] !== undefined && a[2] !== '';
                } else {
                    return true;
                }
            }
            return true;
        }
    },


    // Regle commune DepannageDirectClient
    {
        key: ReclamationQualificationDetailCodes.DepannageDirectClient,
        errorMsg: 'Avez-vous résolu le problème de votre client ?',
        fields: ['reclamationTypeClient', 'utilisateurMagasin', 'depannageDirectClient'],
        assertion: function (a) {
            if (a[0] && a[1]) {
                return a[2] !== undefined && a[2] !== '';
            }
            return true;
        }
    }
];

const Etape = {
    ChoixDeLaPiece: 0,
    Qualifications: 1
};

class ColisAbimeStep extends React.Component {
    state = {
        expandedPanel: 'panelPiece',
        etape: Etape.ChoixDeLaPiece
    };

    componentDidMount() {
        // On détermine si on doit rendre accessible le bouton pour passer à l'étape suivante ou le bouton de création.
        const { reclamation, onNextStepValid } = this.props;

        // On vérifie que la réclamation possède un enregistrement pour chaque détail.
        let nextStepValid = false;
        if (reclamation !== null && reclamation.qualificationDetails.length > 0) {
            const detailColisAbimeType = this.getColisAbimeType();
            const detailReserveTransporteurFaite = this.getReserveTransporteurFaite();
            const detailColisRefuse = this.getColisRefuse();
            const detailPhotoJoignable = this.getPhotoJoignable();

            if (detailColisAbimeType !== '' &&
                detailReserveTransporteurFaite !== '' &&
                ((detailReserveTransporteurFaite === 'false' && detailColisRefuse !== '') || (detailReserveTransporteurFaite === 'true' && detailPhotoJoignable !== '')) &&
                reclamation.numeroPiece.length > 0 &&
                reclamation.lignes.length > 0) {
                nextStepValid = true;
            }
        }

        onNextStepValid(nextStepValid);
    }

    rules = (newState) => {

        let currentState = newState ? newState : this.state;

        let newRules = [..._rules];
        for (let i in currentState.mountedSubComponents) {
            let name = currentState.mountedSubComponents[i];

            newRules.push(
                {
                    key: name,
                    errorMsg: `Erreur(s) dans le sous-composant: ${name}`,
                    fields: ['mountedSubComponentsError'],
                    assertion: function (a) {
                        return !a[0] || !a[0].find(x => x === name);
                    }
                });
        }
        // Pas d'erreur dans les sous-composants démontés
        for (let i in currentState.unMountedSubComponents) {
            let name = currentState.unMountedSubComponents[i];

            newRules.push(
                {
                    key: name,
                    errorMsg: ``,
                    fields: ['mountedSubComponents'],
                    assertion: function (a) {
                        return true;
                    }
                });
        }
        return newRules;
    }

    // Obtention d'un détail quelconque.
    getDetail(code, typeValeur) {
        const { reclamation } = this.props;

        // Initialisation de la valeur
        let valeur = '';

        // Obtention du code
        const detail = reclamation.qualificationDetails.find(detail => detail.code === code);

        // Affectation si possible de la valeur en fonction du type demandé
        if (detail !== undefined) {
            valeur = detail[typeValeur];
        }

        return valeur;
    }


    // Génére un tableau des champs à controler. Un champ peut être mis à jour par rapport aux valeur par défaut.
    getToCheck(champ, valeur) { // TODO à passer en multiple modifications comme pasBonProduit
        const { reclamation, utilisateurMagasin, colisLogistiques } = this.props;


        let result = {
            colisAbimeType: this.getColisAbimeType(),
            reserveTransporteurFaite: this.getReserveTransporteurFaite(),
            colisRefuse: this.getColisRefuse(),
            photoJoignable: this.getPhotoJoignable(),
            photoConsultee: this.getPhotoConsultee(),

            lignes: [...reclamation.lignes],
            colisLogistiques: colisLogistiques,

            utilisateurMagasin: utilisateurMagasin,
            reclamationTypeClient: reclamation.type.code === TypeCodes.Client,
            depannageDirectClient: this.getDepannageDirectClient(),
        }

        if (champ) {
            result[champ] = valeur;
        }
        return result;

    }



    getColisAbimeType() {
        return this.getDetail(ReclamationQualificationDetailCodes.ColisAbimeType, 'valeurInteger');
    }

    getReserveTransporteurFaite() {
        return this.getDetail(ReclamationQualificationDetailCodes.ReserveTransporteurFaite, 'valeurBool');
    }

    getColisRefuse() {
        return this.getDetail(ReclamationQualificationDetailCodes.ColisRefuse, 'valeurBool');
    }

    getDepannageDirectClient() {
        return this.getDetail(ReclamationQualificationDetailCodes.DepannageDirectClient, 'valeurBool');
    }

    getPhotoJoignable() {
        return this.getDetail(ReclamationQualificationDetailCodes.PhotoJoignable, 'valeurBool');
    }

    getPhotoConsultee() {
        return this.getDetail(ReclamationQualificationDetailCodes.PhotoConsultee, 'valeurInteger');
    }

    handleChangePanel = panel => (event, expandedPanel) => {
        this.setState({
            expandedPanel: expandedPanel ? panel : false
        });
    }

    handleChange = (event) => {
        const { onChange } = this.props;

        let toCheckValue = this.getToCheck(event.target.name, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, this.rules());

        onChange(event, errors);
    }

    handleSelectPiece = (value) => {
        const { onSelectPiece } = this.props;

        // On ouvre le panel avec les détails de la qualification.
        this.setState({
            expandedPanel: 'panelQualificationDetails',
            etape: Etape.Qualifications
        });

        onSelectPiece(value, qualificationHelper.getErrors(this.getToCheck(), this.rules()));
    }

    handleQualificationDetailsColisAbimeTypeChange = (event) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 0,
            code: ReclamationQualificationDetailCodes.ColisAbimeType,
            type: ReclamationQualificationDetailType.ChoixSimple,
            libelle: 'État du ou des colis',
            valeurInteger: event.target.value,
            valeurString: event.target.labels[0].innerText
        };


        let toCheckValue = this.getToCheck('colisAbimeType', event.target.value);

        const errors = qualificationHelper.getErrors(toCheckValue, this.rules());


        onDetailChange(qualificationDetail, errors, null);
    }

    handleQualificationDetailsReserveTransporteurFaiteChange = (event) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 1,
            code: ReclamationQualificationDetailCodes.ReserveTransporteurFaite,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: 'Avez-vous effectué une réserve auprès du transporteur ?',
            valeurBool: event.target.value
        };

        let toCheckValue = this.getToCheck('reserveTransporteurFaite', event.target.value);

        const errors = qualificationHelper.getErrors(toCheckValue, this.rules());


        // On ne doit plus tenir compte de la réponse à la question  COLIS_REFUSE / PHOTO_JOIGNABLE
        let detailsToDelete = [];
        if (event.target.value === 'true') {
            detailsToDelete.push(ReclamationQualificationDetailCodes.ColisRefuse);
        } else {
            detailsToDelete.push(ReclamationQualificationDetailCodes.PhotoJoignable);
        }

        onDetailChange(qualificationDetail, errors, detailsToDelete);
    }

    handleQualificationDetailsColisRefuseChange = (event) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 2,
            code: ReclamationQualificationDetailCodes.ColisRefuse,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: 'Avez-vous refusé un ou plusieurs colis ?',
            valeurBool: event.target.value
        };

        let toCheckValue = this.getToCheck('colisRefuse', event.target.value);

        const errors = qualificationHelper.getErrors(toCheckValue, this.rules());

        onDetailChange(qualificationDetail, errors, null);

        // On ouvre le panel qui contient les lignes de la pièce.
        this.setState({
            expandedPanel: 'panelLignes'
        });
    }

    handleQualificationDetailsPhotoJoignableChange = (event) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 2,
            code: ReclamationQualificationDetailCodes.PhotoJoignable,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: 'Pouvez-vous joindre une photo du colis abîmé ?',
            valeurBool: event.target.value
        };

        let toCheckValue = this.getToCheck('photoJoignable', event.target.value);

        const errors = qualificationHelper.getErrors(toCheckValue, this.rules());

        onDetailChange(qualificationDetail, errors, null);

        //On ouvre le panel qui contient les lignes de la pièce.
        this.setState({
            expandedPanel: 'panelLignes'
        });
    }

    handleQualificationDetailsPhotoConsulteeChange = (event) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 3,
            code: ReclamationQualificationDetailCodes.PhotoConsultee,
            type: ReclamationQualificationDetailType.ChoixSimple,
            libelle: photoConsulteeLabel,
            valeurString: photoConsulteeLabels[event.target.value],
            valeurInteger: parseInt(event.target.value, 10)
        };

        let toCheckValue = this.getToCheck('photoConsultee', event.target.value);

        const errors = qualificationHelper.getErrors(toCheckValue, this.rules());


        onDetailChange(qualificationDetail, errors, null);
    }








    handleQualificationDetailsDepannageDirectClientChange = (event) => {
        const { onDetailChange } = this.props;

        const qualificationDetail = {
            position: 4,
            code: ReclamationQualificationDetailCodes.DepannageDirectClient,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: 'Avez-vous résolu le problème de votre client ?',
            valeurBool: event.target.value
        };

        let toCheckValue = this.getToCheck('depannageDirectClient', event.target.value);

        const errors = qualificationHelper.getErrors(toCheckValue, this.rules());


        onDetailChange(qualificationDetail, errors, null);
    }

    handleAddLigne = (ligne) => {
        const { onAddLigne, reclamation } = this.props;

        let newLignes = [...reclamation.lignes];
        newLignes.push(ligne);


        let toCheckValue = this.getToCheck('lignes', newLignes);
        const errors = qualificationHelper.getErrors(toCheckValue, this.rules());

        onAddLigne(ligne, errors);
    }


    handleRemoveLigne = (numeroLigne) => {
        const { onRemoveLigne, reclamation } = this.props;

        onRemoveLigne(numeroLigne, qualificationHelper.getErrors(this.getToCheck('lignes', reclamation.lignes.filter(x => x.numeroLigne !== numeroLigne)), this.rules()));
    }

    renderDetailsQualifications() {
        const { classes } = this.props;

        const detailColisAbimeTypeValue = this.getColisAbimeType();
        const detailReserveTransporteurFaiteValue = this.getReserveTransporteurFaite();
        const detailColisRefuseValue = this.getColisRefuse();
        const detailPhotoJoignableValue = this.getPhotoJoignable();

        return (
            <React.Fragment>
                <div className={classes.row}>
                    <FormControl component="fieldset" className={classes.formControl} required>
                        <FormLabel component="legend">État du ou des colis ?</FormLabel>
                        <RadioGroup
                            name="COLIS_ABIME_TYPE"
                            className={classes.group}
                            value={detailColisAbimeTypeValue}
                            onChange={this.handleQualificationDetailsColisAbimeTypeChange}
                        >
                            <FormControlLabel className={classes.heightLabel} value="0" control={<Radio />} label="Abîmé" />
                            <FormControlLabel className={classes.heightLabel} value="1" control={<Radio />} label="Ouvert" />
                            <FormControlLabel className={classes.heightLabel} value="2" control={<Radio />} label="Troué" />
                            <FormControlLabel className={classes.heightLabel} value="3" control={<Radio />} label="Avec du scotch transporteur" />
                            <FormControlLabel className={classes.heightLabel} value="4" control={<Radio />} label="Ecrasé" />
                        </RadioGroup>
                    </FormControl>
                </div>
                <div className={classes.row}>
                    <FormControl component="fieldset" className={classes.formControl} required>
                        <FormLabel component="legend">Avez-vous effectué une réserve auprès du transporteur ?</FormLabel>
                        <RadioGroup
                            name="RESERVE_TRANSPORTEUR_FAITE"
                            className={classes.group}
                            value={detailReserveTransporteurFaiteValue}
                            onChange={this.handleQualificationDetailsReserveTransporteurFaiteChange}
                        >
                            <FormControlLabel className={classes.heightLabel} value="true" control={<Radio />} label="Oui" />
                            <FormControlLabel className={classes.heightLabel} value="false" control={<Radio />} label="Non" />
                        </RadioGroup>
                    </FormControl>
                </div>


                {(detailReserveTransporteurFaiteValue === 'true') &&
                    <div className={classes.row}>
                        <FormControl component="fieldset" className={classes.formControl} required>
                            <FormLabel component="legend">Pouvez-vous joindre une photo du colis abîmé ?</FormLabel>
                            <RadioGroup
                                name="PHOTO_JOIGNABLE"
                                className={classes.group}
                                value={detailPhotoJoignableValue}
                                onChange={this.handleQualificationDetailsPhotoJoignableChange}
                            >
                                <FormControlLabel className={classes.heightLabel} value="true" control={<Radio />} label="Oui" />
                                <FormControlLabel className={classes.heightLabel} value="false" control={<Radio />} label="Non" />
                            </RadioGroup>
                        </FormControl>
                    </div>
                }

                {(detailReserveTransporteurFaiteValue === 'false') &&
                    <div className={classes.row}>
                        <FormControl component="fieldset" className={classes.formControl} required>
                            <FormLabel component="legend">Avez-vous refusé un ou plusieurs colis ?</FormLabel>
                            <RadioGroup
                                name="COLIS_REFUSE"
                                className={classes.group}
                                value={detailColisRefuseValue}
                                onChange={this.handleQualificationDetailsColisRefuseChange}
                            >
                                <FormControlLabel className={classes.heightLabel} value="true" control={<Radio />} label="Oui" />
                                <FormControlLabel className={classes.heightLabel} value="false" control={<Radio />} label="Non" />
                            </RadioGroup>
                        </FormControl>
                    </div>
                }

            </React.Fragment>
        );
    }

    renderPriseConnaissancePhoto() {
        const { classes, reclamation, colisLogistiques } = this.props;

        const photoConsulteeValue = this.getPhotoConsultee();

        let reclaCodes = reclamation.lignes.map(x => x.codeArticle);
                  


        let colisLogistiquesPhoto = colisLogistiques.map(x => {

            let codesArticles = x.lignes.map(x => x.codeArticle);
            let c = {
                photoId: x.photoId,
                numeroSupport: x.numeroSupport,
                concerneRecla: reclaCodes.some(r => codesArticles.indexOf(r) >= 0)
            };
            return c;
        });

        //console.log("colisLogistiquesPhoto", colisLogistiquesPhoto);

        let afficherQuestion = colisLogistiquesPhoto.some(x => x.concerneRecla && x.photoId);

        return (

            <>

                {afficherQuestion && (
                    <>

                        <div className={classes.row}>

                            <FormControl required className={classes.formControl}>
                                <FormLabel component="legend" sx={{display:'flex'}}>
                                    {photoConsulteeLabel}

                                    {colisLogistiquesPhoto.map((colis, index) => {
                                        return (
                                            <ColisLogistiquePhoto
                                                key={index}
                                                colis={colis}
                                                disabledMessage={!colis.photoId ? "pas de photo de ce colis" : !colis.concerneRecla ? "colis non concerné" : ""}
                                                mode={"button"}
                                                size={"large"}
                                            />
                                        );
                                    })}                                    

                                </FormLabel>
                                <RadioGroup
                                    id={ReclamationQualificationDetailCodes.PhotoConsultee}
                                    name={ReclamationQualificationDetailCodes.PhotoConsultee}
                                    className={classes.group}
                                    value={photoConsulteeValue}
                                    onChange={this.handleQualificationDetailsPhotoConsulteeChange}>
                                    {photoConsulteeLabels.map((reponse, index) => (
                                        <FormControlLabel key={index} value={index.toString()} control={<Radio />} label={reponse} />
                                    ))}
                                </RadioGroup>
                            </FormControl>

                        </div>

                    </>
                )}
            </>
            
            
       );

    }

    renderDepannageDirectClient() {
        const { reclamation, utilisateurMagasin } = this.props;

        const detailDepannageDirectClientValue = this.getDepannageDirectClient();

        if (reclamation.type.code === TypeCodes.Client && utilisateurMagasin) {
            return (<DepannageDirectClient
                onDetailChange={this.handleQualificationDetailsDepannageDirectClientChange}
                value={detailDepannageDirectClientValue}
            />
            );
        } else {
            return null;
        }
    }

    render() {
        const { classes, reclamation, namespace } = this.props;
        const { etape, expandedPanel } = this.state;

        return (
            <React.Fragment>

                <div className={classes.row}>
                    <TextField
                        multiline
                        fullWidth
                        name="commentaire"
                        label="Commentaire"
                        placeholder="Décrire le problème rencontré"
                        maxRows="3"
                        onChange={this.handleChange}
                        value={reclamation.commentaire}
                        InputLabelProps={{
                            shrink: true
                        }}
                    />
                </div>

                <div className={classes.row}>
                    <DropZone id="fichiersIds" name="fichiersIds" onChange={this.handleChange} />
                </div>


                <div className={classes.row}>
                    <Accordion expanded={expandedPanel === 'panelPiece'} onChange={this.handleChangePanel('panelPiece')}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            className={classes.panelSummary}
                        >
                            <Typography >Rechercher une pièce* (obligatoire pour accéder aux lignes)</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <PieceGestionSearch
                                onSelectPiece={this.handleSelectPiece}
                                client={reclamation.client}
                                withLignes
                                namespace={namespace}
                                warningReclamation
                                defautProduitInfos={false}
                                logistiqueInfos={false}
                                defautNumeroPiece={reclamation.numeroPiece}
                            />
                        </AccordionDetails>
                    </Accordion>



                    {reclamation.numeroPiece && etape === Etape.Qualifications && (

                        <>

                            <Accordion expanded={expandedPanel === 'panelQualificationDetails'} onChange={this.handleChangePanel('panelQualificationDetails')}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    className={classes.panelSummary}
                                >
                                    <Typography >Qualification</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.flexContainer}>
                                        {this.renderDetailsQualifications()}
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                            <Accordion sx={{ mt: 2 }} expanded={expandedPanel === 'panelLignes'} onChange={this.handleChangePanel('panelLignes')}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    className={classes.panelSummary}
                                >
                                    <Typography >Sélectionner une ligne* (au moins une ligne en anomalie pour pouvoir créer le dossier après-vente)</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.row}>
                                        <LigneGestionQualification
                                            reclamation={reclamation}
                                            onAddLigne={this.handleAddLigne}
                                            onRemoveLigne={this.handleRemoveLigne}
                                            typeAnomalie={AnomalieType.ColisAbime}
                                            disableCompose
                                            namespace={namespace}
                                            afficherQuantiteRetournable
                                        />

                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            <div className={classes.row}>
                                {this.renderPriseConnaissancePhoto()}
                            </div>

                            <div className={classes.row}>
                                {this.renderDepannageDirectClient()}
                            </div>

                        </>

                    )}



                </div>
            </React.Fragment>
        );
    }
}

ColisAbimeStep.propTypes = {
    reclamation: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onDetailChange: PropTypes.func.isRequired,
    onSelectPiece: PropTypes.func.isRequired,
    onAddLigne: PropTypes.func.isRequired,
    onRemoveLigne: PropTypes.func.isRequired,
    validationErrors: PropTypes.array,
    onNextStepValid: PropTypes.func.isRequired,
    namespace: PropTypes.string.isRequired
};

export default withStyles(styles)(ColisAbimeStep);